import { createContext, useContext, useState, useEffect, lazy, Suspense, Component } from 'react'
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom'
import { api, logout as apiLogout, setWorkspaceId, getWorkspaceId, setAccessToken, clearAccessToken } from './api/client.js'
import { G } from './theme.js'

const Landing = lazy(() => import('./pages/Landing.jsx'))
const Login = lazy(() => import('./pages/Login.jsx'))
const Register = lazy(() => import('./pages/Register.jsx'))
const GuidedWealthHome = lazy(() => import('./pages/GuidedWealthHome.jsx'))
const GuidedWealthJourney = lazy(() => import('./pages/GuidedWealthJourney.jsx'))
const Dashboard = lazy(() => import('./pages/Dashboard.jsx'))
const Assets = lazy(() => import('./pages/Assets.jsx'))
const Liabilities = lazy(() => import('./pages/Liabilities.jsx'))
const Insights = lazy(() => import('./pages/Insights.jsx'))
const Settings = lazy(() => import('./pages/Settings.jsx'))
const Transactions = lazy(() => import('./pages/Transactions.jsx'))
const Goals = lazy(() => import('./pages/Goals.jsx'))
const Admin = lazy(() => import('./pages/Admin.jsx'))
const Connect = lazy(() => import('./pages/Connect.jsx'))
const Upload = lazy(() => import('./pages/Upload.jsx'))
const Household = lazy(() => import('./pages/Household.jsx'))
const JoinHousehold = lazy(() => import('./pages/JoinHousehold.jsx'))
const Tax = lazy(() => import('./pages/Tax.jsx'))
const Insurance = lazy(() => import('./pages/Insurance.jsx'))
const Budget = lazy(() => import('./pages/Budget.jsx'))
const Investments = lazy(() => import('./pages/Investments.jsx'))
const Notifications = lazy(() => import('./pages/Notifications.jsx'))
const BrokerCallback = lazy(() => import('./pages/BrokerCallback.jsx'))
const Organization = lazy(() => import('./pages/Organization.jsx'))
const SecurityCenter = lazy(() => import('./pages/SecurityCenter.jsx'))
const AuditLog = lazy(() => import('./pages/AuditLog.jsx'))
const BillingPortal = lazy(() => import('./pages/BillingPortal.jsx'))
const AccessProvisioning = lazy(() => import('./pages/AccessProvisioning.jsx'))
const VerifiedNetWorth = lazy(() => import('./pages/VerifiedNetWorth.jsx'))
const TrustCenter = lazy(() => import('./pages/TrustCenter.jsx'))
const FamilyOffice = lazy(() => import('./pages/FamilyOffice.jsx'))
const AdvisorDesk = lazy(() => import('./pages/AdvisorDesk.jsx'))
const FinancialPlanStudio = lazy(() => import('./pages/FinancialPlanStudio.jsx'))
const PortfolioRebalanceSimulator = lazy(() => import('./pages/PortfolioRebalanceSimulator.jsx'))
const TaxOpportunityStudio = lazy(() => import('./pages/TaxOpportunityStudio.jsx'))
const WealthReportStudio = lazy(() => import('./pages/WealthReportStudio.jsx'))
const ClientOnboardingStudio = lazy(() => import('./pages/ClientOnboardingStudio.jsx'))
const ImpactDashboard = lazy(() => import('./pages/ImpactDashboard.jsx'))
const AnalyticsHub = lazy(() => import('./pages/AnalyticsHub.jsx'))
const AnalyticsReadiness = lazy(() => import('./pages/AnalyticsReadiness.jsx'))
const PortfolioXray = lazy(() => import('./pages/PortfolioXray.jsx'))
const Performance = lazy(() => import('./pages/Performance.jsx'))
const RiskAnalytics = lazy(() => import('./pages/RiskAnalytics.jsx'))
const SpendAnalytics = lazy(() => import('./pages/SpendAnalytics.jsx'))
const GoalSimulator = lazy(() => import('./pages/GoalSimulator.jsx'))
const PeerBenchmark = lazy(() => import('./pages/PeerBenchmark.jsx'))
const PilotKit = lazy(() => import('./pages/PilotKit.jsx'))
const PilotReadiness = lazy(() => import('./pages/PilotReadiness.jsx'))
const AssistedOnboarding = lazy(() => import('./pages/AssistedOnboarding.jsx'))
const LegalPage = lazy(() => import('./pages/LegalPage.jsx'))
const Layout = lazy(() => import('./components/Layout.jsx'))
const MarketPrices = lazy(() => import('./pages/MarketPrices.jsx'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword.jsx').then((m) => ({ default: m.ForgotPassword })))
const ResetPassword = lazy(() => import('./pages/ForgotPassword.jsx').then((m) => ({ default: m.ResetPassword })))

const defaultAuthContext = {
  user: null,
  plan: 'free',
  limits: { maxAssets: 5, maxLiabs: 3, insights: false },
  login: async () => {},
  logout: () => {},
  refreshPlan: async () => {},
  switchWorkspace: async () => {},
}

const AuthCtx = createContext(defaultAuthContext)
export const useAuth = () => useContext(AuthCtx) || defaultAuthContext
const normalizeUser = (user) => user ? { ...user, emailVerified: user.emailVerified ?? user.email_verified ?? false } : null

function Splash() {
  return <div style={{ background: G.bg, height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div style={{ width: 24, height: 24, border: `2px solid ${G.border}`, borderTopColor: G.gold, borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} /></div>
}

function RouteShell({ children }) { return <Suspense fallback={<Splash />}>{children}</Suspense> }

const ROLE_RANK = { viewer: 10, support: 15, editor: 20, advisor: 25, admin: 30, billing_admin: 35, owner: 40 }
function roleRank(role) { return ROLE_RANK[String(role || 'viewer')] || 0 }
function hasAnyRole(role, allowed = []) {
  if (!allowed.length) return true
  if (role === 'owner' || role === 'admin') return true
  return allowed.includes(role)
}
function AccessDenied() {
  return <div style={{ padding: 40, background: G.bg, color: G.text, minHeight: '100vh' }}>
    <div style={{ maxWidth: 720, margin: '0 auto', background: G.card, border: `1px solid ${G.border}`, borderRadius: 18, padding: 24 }}>
      <h1 style={{ marginTop: 0 }}>Access not available for this role</h1>
      <p style={{ color: G.sub, lineHeight: 1.6 }}>This area is role-protected. Ask an organization owner or admin to grant the right workspace role.</p>
    </div>
  </div>
}
function RequireAnyRole({ allowed = [], minRole = null, children }) {
  const { user } = useAuth()
  const role = user?.role || 'viewer'
  const ok = minRole ? roleRank(role) >= roleRank(minRole) || role === 'owner' || role === 'admin' : hasAnyRole(role, allowed)
  return ok ? children : <AccessDenied />
}


function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [plan, setPlan] = useState('free')
  const [limits, setLimits] = useState({ maxAssets: 5, maxLiabs: 3, insights: false })
  const [loading, setLoading] = useState(true)

  const applyAuthPayload = (payload) => {
    const nextUser = normalizeUser(payload.user || payload)
    setUser(nextUser)
    setPlan(payload.plan || payload.user?.plan || 'free')
    setLimits(payload.limits || { maxAssets: 5, maxLiabs: 3, insights: false })
    const wid = nextUser?.workspace?.id || nextUser?.workspace_id || getWorkspaceId()
    if (wid) setWorkspaceId(wid)
  }

  const bootstrap = async () => {
    const timer = setTimeout(() => setLoading(false), 3000)
    try {
      const r = await api.post('/auth/refresh')
      if (r.data?.token) {
        setAccessToken(r.data.token)
        // Refresh already returns user/plan/limits — avoid a second round-trip that can race or 401
        applyAuthPayload(r.data)
      }
    } catch {
      clearAccessToken()
      localStorage.removeItem('wv_workspace_id')
      setUser(null)
    } finally {
      clearTimeout(timer)
      setLoading(false)
    }
  }
  useEffect(() => { bootstrap() }, [])

  const login = async (token, payload) => { setAccessToken(token); applyAuthPayload(payload) }
  const refreshPlan = async () => { try { const r = await api.get('/plan'); setPlan(r.data.plan); setLimits(r.data.limits) } catch {} }
  const switchWorkspace = (workspaceId) => { setWorkspaceId(workspaceId); setUser((u) => u ? ({ ...u, workspace: { ...(u.workspace || {}), id: workspaceId } }) : u) }
  const logout = async () => { try { await apiLogout() } catch {} clearAccessToken(); localStorage.removeItem('wv_workspace_id'); setUser(null); setPlan('free'); setLimits({ maxAssets: 5, maxLiabs: 3, insights: false }) }

  if (loading) return <Splash />
  return <AuthCtx.Provider value={{ user, plan, limits, login, logout, refreshPlan, switchWorkspace }}>{children}</AuthCtx.Provider>
}

function RequireAuth() { const { user } = useAuth(); return user ? <Outlet /> : <Navigate to='/login' replace /> }
function RequireGuest() { const { user } = useAuth(); return user ? <Navigate to='/app' replace /> : <Outlet /> }

class GlobalErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, error: null }
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }
  componentDidCatch(error, errorInfo) {
    console.error('[GlobalErrorBoundary] Caught rendering error:', error, errorInfo)
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, background: G.bg, color: G.text, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Outfit',sans-serif" }}>
          <div style={{ maxWidth: 560, width: '100%', background: G.card, border: `1px solid ${G.border}`, borderRadius: 16, padding: '36px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, marginBottom: 10 }}>Application Interface Error</h2>
            <p style={{ fontSize: 13, color: G.sub, lineHeight: 1.6, marginBottom: 16 }}>
              An unexpected UI rendering error occurred.
            </p>
            {this.state.error?.message && (
              <div style={{ background: '#2B1515', border: '1px solid #7F1D1D', color: '#FCA5A5', padding: 12, borderRadius: 8, fontSize: 12, fontFamily: 'monospace', textAlign: 'left', marginBottom: 20, overflowX: 'auto', maxHeight: 120 }}>
                {this.state.error.message}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => window.location.reload()} style={{ background: G.gold, color: '#090B11', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                🔄 Reload App
              </button>
              <button onClick={async () => {
                try {
                  if ('caches' in window) {
                    const keys = await caches.keys()
                    await Promise.all(keys.map((k) => caches.delete(k)))
                  }
                  if ('serviceWorker' in navigator) {
                    const regs = await navigator.serviceWorker.getRegistrations()
                    await Promise.all(regs.map((r) => r.unregister()))
                  }
                } catch {}
                localStorage.clear()
                sessionStorage.clear()
                window.location.href = '/app'
              }} style={{ background: G.s2, color: G.text, border: `1px solid ${G.border}`, borderRadius: 8, padding: '10px 18px', fontSize: 13, cursor: 'pointer' }}>
                🧹 Reset Session & Reload
              </button>
            </div>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

export default function App() {
  return (
    <GlobalErrorBoundary>
      <AuthProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <Routes>
          <Route path='/' element={<RouteShell><Landing /></RouteShell>} />
          <Route path='/legal/:doc' element={<RouteShell><LegalPage /></RouteShell>} />
          <Route path='/join/:token' element={<RouteShell><JoinHousehold /></RouteShell>} />
          <Route path='/forgot-password' element={<RouteShell><ForgotPassword /></RouteShell>} />
          <Route path='/reset-password' element={<RouteShell><ResetPassword /></RouteShell>} />
          <Route element={<RequireGuest />}>
            <Route path='/login' element={<RouteShell><Login /></RouteShell>} />
            <Route path='/register' element={<RouteShell><Register /></RouteShell>} />
          </Route>
          <Route element={<RequireAuth />}>
            <Route element={<RouteShell><Layout /></RouteShell>}>
              <Route path='/app' element={<RouteShell><GuidedWealthHome /></RouteShell>} />
              <Route path='/app/start' element={<RouteShell><GuidedWealthJourney /></RouteShell>} />
              <Route path='/app/dashboard' element={<RouteShell><Dashboard /></RouteShell>} />
              <Route path='/app/pilot-kit' element={<RouteShell><PilotKit /></RouteShell>} />
              <Route path='/app/pilot-readiness' element={<RouteShell><PilotReadiness /></RouteShell>} />
              <Route path='/app/onboarding' element={<RouteShell><AssistedOnboarding /></RouteShell>} />
              <Route path='/app/verified-net-worth' element={<RouteShell><VerifiedNetWorth /></RouteShell>} />
              <Route path='/app/market-prices' element={<RouteShell><MarketPrices /></RouteShell>} />
              <Route path='/app/trust-center' element={<RouteShell><TrustCenter /></RouteShell>} />
              <Route path='/app/assets' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><Assets /></RequireAnyRole></RouteShell>} />
              <Route path='/app/liabilities' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><Liabilities /></RequireAnyRole></RouteShell>} />
              <Route path='/app/transactions' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><Transactions /></RequireAnyRole></RouteShell>} />
              <Route path='/app/analytics' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><AnalyticsHub /></RequireAnyRole></RouteShell>} />
              <Route path='/app/analytics/readiness' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><AnalyticsReadiness /></RequireAnyRole></RouteShell>} />
              <Route path='/app/analytics/xray' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><PortfolioXray /></RequireAnyRole></RouteShell>} />
              <Route path='/app/analytics/performance' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><Performance /></RequireAnyRole></RouteShell>} />
              <Route path='/app/analytics/risk' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><RiskAnalytics /></RequireAnyRole></RouteShell>} />
              <Route path='/app/analytics/spend' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><SpendAnalytics /></RequireAnyRole></RouteShell>} />
              <Route path='/app/analytics/goals' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><GoalSimulator /></RequireAnyRole></RouteShell>} />
              <Route path='/app/analytics/goals/:id' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><GoalSimulator /></RequireAnyRole></RouteShell>} />
              <Route path='/app/analytics/peer' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><PeerBenchmark /></RequireAnyRole></RouteShell>} />
              <Route path='/app/goals' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><Goals /></RequireAnyRole></RouteShell>} />
              <Route path='/app/budget' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><Budget /></RequireAnyRole></RouteShell>} />
              <Route path='/app/investments' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><Investments /></RequireAnyRole></RouteShell>} />
              <Route path='/app/tax' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><Tax /></RequireAnyRole></RouteShell>} />
              <Route path='/app/insurance' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><Insurance /></RequireAnyRole></RouteShell>} />
              <Route path='/app/household' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><Household /></RequireAnyRole></RouteShell>} />
              <Route path='/app/family-office' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><FamilyOffice /></RequireAnyRole></RouteShell>} />
              <Route path='/app/connect' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><Connect /></RequireAnyRole></RouteShell>} />
              <Route path='/app/connect/callback/:provider' element={<RouteShell><BrokerCallback /></RouteShell>} />
              <Route path='/app/upload' element={<RouteShell><RequireAnyRole minRole='editor'><Upload /></RequireAnyRole></RouteShell>} />
              <Route path='/app/notifications' element={<RouteShell><Notifications /></RouteShell>} />
              <Route path='/app/insights' element={<RouteShell><Insights /></RouteShell>} />
              <Route path='/app/advisor' element={<RouteShell><RequireAnyRole minRole='editor'><AdvisorDesk /></RequireAnyRole></RouteShell>} />
              <Route path='/app/advisor/financial-plan' element={<RouteShell><RequireAnyRole minRole='editor'><FinancialPlanStudio /></RequireAnyRole></RouteShell>} />
              <Route path='/app/advisor/portfolio-rebalance' element={<RouteShell><RequireAnyRole minRole='editor'><PortfolioRebalanceSimulator /></RequireAnyRole></RouteShell>} />
              <Route path='/app/advisor/tax-opportunities' element={<RouteShell><RequireAnyRole minRole='editor'><TaxOpportunityStudio /></RequireAnyRole></RouteShell>} />
              <Route path='/app/advisor/reports' element={<RouteShell><RequireAnyRole minRole='editor'><WealthReportStudio /></RequireAnyRole></RouteShell>} />
              <Route path='/app/advisor/onboarding' element={<RouteShell><RequireAnyRole minRole='editor'><ClientOnboardingStudio /></RequireAnyRole></RouteShell>} />
              <Route path='/app/impact' element={<RouteShell><RequireAnyRole allowed={['editor','billing_admin']}><ImpactDashboard /></RequireAnyRole></RouteShell>} />
              <Route path='/app/organization' element={<RouteShell><RequireAnyRole allowed={['admin','owner']}><Organization /></RequireAnyRole></RouteShell>} />
              <Route path='/app/billing' element={<RouteShell><RequireAnyRole allowed={['billing_admin']}><BillingPortal /></RequireAnyRole></RouteShell>} />
              <Route path='/app/access' element={<RouteShell><RequireAnyRole allowed={['admin','owner']}><AccessProvisioning /></RequireAnyRole></RouteShell>} />
              <Route path='/app/security' element={<RouteShell><RequireAnyRole allowed={['admin','owner']}><SecurityCenter /></RequireAnyRole></RouteShell>} />
              <Route path='/app/audit' element={<RouteShell><RequireAnyRole allowed={['admin','owner']}><AuditLog /></RequireAnyRole></RouteShell>} />
              <Route path='/app/settings' element={<RouteShell><Settings /></RouteShell>} />
              <Route path='/app/admin' element={<RouteShell><RequireAnyRole allowed={['admin','owner']}><Admin /></RequireAnyRole></RouteShell>} />
            </Route>
          </Route>
          <Route path='*' element={<Navigate to='/' replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  </GlobalErrorBoundary>
  )
}
